import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Plus, Trash2, GripVertical, CheckCircle2, Sparkles, Loader2, Upload, FileText,
  Settings, ToggleLeft, CheckSquare, CircleDot, ChevronDown, MessageSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { uploadAndParseDocument } from '@/lib/documentParser';

export type QuestionType = 'multiple_choice' | 'true_false' | 'multi_select' | 'free_text';

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_answer: number | number[]; // Support multi-select
  explanation?: string;
  points?: number; // Per-question marks (default: 1)
  question_type?: QuestionType;
}

export interface QuizConfig {
  quiz_type: 'multiple_choice' | 'true_false' | 'mixed';
  passing_score: number;
  questions: QuizQuestion[];
  time_limit_minutes?: number;
  show_explanations?: boolean;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
}

interface QuizBuilderProps {
  value: QuizConfig;
  onChange: (config: QuizConfig) => void;
}

const QUESTION_TYPE_ICONS: Record<QuestionType, React.ReactNode> = {
  multiple_choice: <CircleDot className="h-3 w-3" />,
  true_false: <ToggleLeft className="h-3 w-3" />,
  multi_select: <CheckSquare className="h-3 w-3" />,
  free_text: <MessageSquare className="h-3 w-3" />,
};

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  multiple_choice: 'MC',
  true_false: 'T/F',
  multi_select: 'Multi',
  free_text: 'Text',
};

export function QuizBuilder({ value, onChange }: QuizBuilderProps) {
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [buildMode, setBuildMode] = useState<'manual' | 'ai'>('manual');
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // AI Generation state
  const [aiTopic, setAiTopic] = useState('');
  const [aiNumQuestions, setAiNumQuestions] = useState(5);
  const [aiDifficulty, setAiDifficulty] = useState('intermediate');
  const [aiQuestionType, setAiQuestionType] = useState<'multiple_choice' | 'true_false' | 'mixed'>('multiple_choice');
  const [isGenerating, setIsGenerating] = useState(false);
  const [referenceDoc, setReferenceDoc] = useState<{ name: string; text: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Calculate total points
  const totalPoints = value.questions.reduce((sum, q) => sum + (q.points || 1), 0);

  const addQuestion = (type: QuestionType = 'multiple_choice') => {
    const defaultOptions = type === 'true_false'
      ? ['True', 'False']
      : type === 'free_text'
        ? []
        : ['', '', '', ''];

    const newQuestion: QuizQuestion = {
      id: crypto.randomUUID(),
      question: '',
      options: defaultOptions,
      correct_answer: type === 'multi_select' ? [] : type === 'free_text' ? -1 : 0,
      points: 1,
      question_type: type,
    };
    onChange({
      ...value,
      questions: [...value.questions, newQuestion],
    });
    setExpandedQuestion(newQuestion.id);
  };

  const updateQuestion = (questionId: string, updates: Partial<QuizQuestion>) => {
    onChange({
      ...value,
      questions: value.questions.map((q) =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    });
  };

  const changeQuestionType = (questionId: string, newType: QuestionType) => {
    const question = value.questions.find((q) => q.id === questionId);
    if (!question) return;

    let newOptions = question.options;
    let newCorrectAnswer: number | number[] = 0;

    if (newType === 'free_text') {
      newOptions = [];
      newCorrectAnswer = -1;
    } else if (newType === 'true_false') {
      newOptions = ['True', 'False'];
      newCorrectAnswer = 0;
    } else if (newType === 'multi_select') {
      if (question.options.length < 4) {
        newOptions = [...question.options, ...Array(4 - question.options.length).fill('')].slice(0, 4);
      }
      newCorrectAnswer = [];
    } else {
      if (question.options.length < 4) {
        newOptions = [...question.options, ...Array(4 - question.options.length).fill('')].slice(0, 4);
      }
      newCorrectAnswer = 0;
    }

    updateQuestion(questionId, {
      question_type: newType,
      options: newOptions,
      correct_answer: newCorrectAnswer,
    });
  };

  const removeQuestion = (questionId: string) => {
    onChange({
      ...value,
      questions: value.questions.filter((q) => q.id !== questionId),
    });
  };

  const updateOption = (questionId: string, optionIndex: number, text: string) => {
    const question = value.questions.find((q) => q.id === questionId);
    if (!question) return;
    
    const newOptions = [...question.options];
    newOptions[optionIndex] = text;
    updateQuestion(questionId, { options: newOptions });
  };

  const toggleMultiSelectAnswer = (questionId: string, optionIndex: number) => {
    const question = value.questions.find((q) => q.id === questionId);
    if (!question) return;

    const currentAnswers = Array.isArray(question.correct_answer) 
      ? question.correct_answer 
      : [question.correct_answer];
    
    const newAnswers = currentAnswers.includes(optionIndex)
      ? currentAnswers.filter(a => a !== optionIndex)
      : [...currentAnswers, optionIndex].sort();
    
    updateQuestion(questionId, { correct_answer: newAnswers });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 25MB.');
      return;
    }

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, DOCX, or TXT files.');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadAndParseDocument(file, 'knowledge-sources', 'quiz-references');
      
      if (!result.success || !result.extracted_text) {
        toast.error(result.error || 'Failed to parse document');
        return;
      }

      setReferenceDoc({
        name: result.name,
        text: result.extracted_text
      });
      toast.success(`Loaded "${result.name}" (${result.word_count} words)`);
    } catch (err) {
      toast.error('Failed to upload document');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic for the quiz');
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-content', {
        body: {
          field: 'quiz_questions',
          context: {
            topic: aiTopic,
            numQuestions: String(aiNumQuestions),
            difficulty: aiDifficulty,
            questionType: aiQuestionType,
          },
          customPrompt: `Generate exactly ${aiNumQuestions} quiz questions about the following topic:

Topic: ${aiTopic}
Difficulty: ${aiDifficulty}
Question Type: ${aiQuestionType === 'true_false' ? 'True/False only' : aiQuestionType === 'mixed' ? 'Mix of multiple choice and true/false' : 'Multiple choice with 4 options'}

${referenceDoc ? `Reference Material:\n${referenceDoc.text.substring(0, 8000)}` : ''}

IMPORTANT: Return ONLY a valid JSON array with no additional text. Each question object must have:
- "question": string (the question text)
- "options": string[] (array of 4 answer options for multiple choice, or ["True", "False"] for true/false)
- "correct_answer": number (0-indexed position of correct answer)
- "explanation": string (brief explanation of why the answer is correct)
- "points": number (1 for easy, 2 for medium, 3 for hard questions)
- "question_type": "multiple_choice" | "true_false"

Example format:
[
  {
    "question": "What is the primary purpose of discovery calls?",
    "options": ["To close the deal immediately", "To understand customer needs and pain points", "To present product features", "To negotiate pricing"],
    "correct_answer": 1,
    "explanation": "Discovery calls are designed to uncover customer needs, challenges, and goals before presenting solutions.",
    "points": 1,
    "question_type": "multiple_choice"
  }
]

Generate ${aiNumQuestions} questions now:`,
          variations: 1,
          category: 'agent'
        }
      });

      if (error) throw error;
      if (!data?.success || !data?.results?.[0]) {
        throw new Error('Failed to generate questions');
      }

      // Parse the JSON response
      let generatedQuestions: any[];
      try {
        // Try to extract JSON from the response
        const responseText = data.results[0];
        const jsonMatch = responseText.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          generatedQuestions = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON array found in response');
        }
      } catch (parseErr) {
        console.error('Parse error:', parseErr, 'Response:', data.results[0]);
        throw new Error('Failed to parse AI response. Please try again.');
      }

      // Convert to QuizQuestion format
      const newQuestions: QuizQuestion[] = generatedQuestions.map((q: any) => ({
        id: crypto.randomUUID(),
        question: q.question || '',
        options: q.options || ['', '', '', ''],
        correct_answer: typeof q.correct_answer === 'number' ? q.correct_answer : 0,
        explanation: q.explanation || '',
        points: typeof q.points === 'number' ? q.points : 1,
        question_type: q.question_type || 'multiple_choice',
      }));

      // Add to existing questions
      onChange({
        ...value,
        quiz_type: aiQuestionType,
        questions: [...value.questions, ...newQuestions]
      });

      toast.success(`Generated ${newQuestions.length} questions! Review and edit as needed.`);
      setBuildMode('manual'); // Switch to manual to show/edit questions
    } catch (err: any) {
      console.error('AI generation error:', err);
      toast.error(err.message || 'Failed to generate questions');
    } finally {
      setIsGenerating(false);
    }
  };

  const getCorrectAnswerDisplay = (question: QuizQuestion) => {
    if (question.question_type === 'multi_select' && Array.isArray(question.correct_answer)) {
      return question.correct_answer.map(i => question.options[i]).filter(Boolean).join(', ');
    }
    return question.options[question.correct_answer as number];
  };

  return (
    <div className="space-y-4">
      {/* Build Mode Toggle */}
      <Tabs value={buildMode} onValueChange={(v) => setBuildMode(v as 'manual' | 'ai')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="manual" className="gap-2">
            <Plus className="h-4 w-4" />
            Manual Build
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2">
            <Sparkles className="h-4 w-4" />
            AI Generate
          </TabsTrigger>
        </TabsList>

        {/* AI Generation Tab */}
        <TabsContent value="ai" className="space-y-4 mt-4">
          <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
            <h4 className="font-medium flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Quiz Generator
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Describe the topic and we'll generate quiz questions for you. You can review and edit them before saving.
            </p>

            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>Topic / Context *</Label>
                <Textarea
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="e.g., MEDDPICC sales methodology, focusing on the Champion and Economic Buyer components..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Questions</Label>
                  <Select 
                    value={String(aiNumQuestions)} 
                    onValueChange={(v) => setAiNumQuestions(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 questions</SelectItem>
                      <SelectItem value="5">5 questions</SelectItem>
                      <SelectItem value="10">10 questions</SelectItem>
                      <SelectItem value="15">15 questions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Type</Label>
                  <Select value={aiQuestionType} onValueChange={(v: any) => setAiQuestionType(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                      <SelectItem value="true_false">True/False</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Difficulty</Label>
                  <Select value={aiDifficulty} onValueChange={setAiDifficulty}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Reference Document Upload */}
              <div className="grid gap-2">
                <Label>Reference Document (Optional)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="quiz-ref-upload"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('quiz-ref-upload')?.click()}
                    disabled={isUploading}
                    className="gap-2"
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Upload Document
                  </Button>
                  {referenceDoc && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      {referenceDoc.name}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setReferenceDoc(null)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload training materials to generate questions based on specific content (PDF, DOCX, TXT)
                </p>
              </div>

              <Button
                type="button"
                onClick={handleAIGenerate}
                disabled={isGenerating || !aiTopic.trim()}
                className="w-full gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating Questions...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Questions
                  </>
                )}
              </Button>
            </div>
          </div>

          {value.questions.length > 0 && (
            <p className="text-sm text-muted-foreground text-center">
              You have {value.questions.length} question{value.questions.length !== 1 ? 's' : ''}. 
              Switch to "Manual Build" to edit them.
            </p>
          )}
        </TabsContent>

        {/* Manual Build Tab */}
        <TabsContent value="manual" className="space-y-4 mt-4">
          {/* Advanced Settings Collapsible */}
          <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="w-full justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Quiz Settings
                </span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", settingsOpen && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <Card className="p-4 bg-muted/30">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="grid gap-2">
                    <Label>Quiz Type</Label>
                    <Select
                      value={value.quiz_type}
                      onValueChange={(type: QuizConfig['quiz_type']) =>
                        onChange({ ...value, quiz_type: type })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        <SelectItem value="true_false">True/False</SelectItem>
                        <SelectItem value="mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Passing Score (%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={value.passing_score}
                      onChange={(e) =>
                        onChange({ ...value, passing_score: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Shuffle Questions</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={value.shuffle_questions ?? false}
                        onCheckedChange={(checked) => onChange({ ...value, shuffle_questions: checked })}
                      />
                      <span className="text-sm text-muted-foreground">
                        {value.shuffle_questions ? 'On' : 'Off'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Shuffle Options</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={value.shuffle_options ?? false}
                        onCheckedChange={(checked) => onChange({ ...value, shuffle_options: checked })}
                      />
                      <span className="text-sm text-muted-foreground">
                        {value.shuffle_options ? 'On' : 'Off'}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </CollapsibleContent>
          </Collapsible>

          {/* Questions */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base">Questions ({value.questions.length})</Label>
              <div className="flex items-center gap-2">
                <Select
                  value=""
                  onValueChange={(type: QuestionType) => addQuestion(type)}
                >
                  <SelectTrigger className="w-[180px]">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">
                      <span className="flex items-center gap-2">
                        <CircleDot className="h-4 w-4" />
                        Multiple Choice
                      </span>
                    </SelectItem>
                    <SelectItem value="true_false">
                      <span className="flex items-center gap-2">
                        <ToggleLeft className="h-4 w-4" />
                        True/False
                      </span>
                    </SelectItem>
                    <SelectItem value="multi_select">
                      <span className="flex items-center gap-2">
                        <CheckSquare className="h-4 w-4" />
                        Multi-Select
                      </span>
                    </SelectItem>
                    <SelectItem value="free_text">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Free Text
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {value.questions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center text-muted-foreground">
                  <p>No questions yet. Click "Add Question" or use AI Generate.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {value.questions.map((question, index) => (
                  <Card
                    key={question.id}
                    className={cn(
                      "transition-all overflow-hidden",
                      expandedQuestion === question.id && "ring-2 ring-primary"
                    )}
                  >
                    <CardContent className="p-4 overflow-hidden">
                      <div
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() =>
                          setExpandedQuestion(
                            expandedQuestion === question.id ? null : question.id
                          )
                        }
                      >
                        <GripVertical className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          {/* Header row with badges */}
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-sm flex-shrink-0">Q{index + 1}</span>
                            <Badge variant="outline" className="gap-1 text-xs flex-shrink-0">
                              {QUESTION_TYPE_ICONS[question.question_type || 'multiple_choice']}
                              {QUESTION_TYPE_LABELS[question.question_type || 'multiple_choice']}
                            </Badge>
                            <Badge variant="secondary" className="text-xs flex-shrink-0">
                              {question.points || 1} pt{(question.points || 1) !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          {/* Question text on separate line */}
                          <p className="text-sm text-muted-foreground line-clamp-2 break-words w-full">
                            {question.question || <span className="italic">Enter question text...</span>}
                          </p>
                          {question.question && getCorrectAnswerDisplay(question) && (
                            <p className="text-xs text-green-600 flex items-center gap-1 mt-1 w-full">
                              <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                              <span className="break-words line-clamp-1">Answer: {getCorrectAnswerDisplay(question)}</span>
                            </p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeQuestion(question.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Expanded Question Editor */}
                      {expandedQuestion === question.id && (
                        <div className="mt-4 pt-4 border-t space-y-4">
                          {/* Question Type & Points Row */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label>Question Type</Label>
                              <Select
                                value={question.question_type || 'multiple_choice'}
                                onValueChange={(type: QuestionType) => changeQuestionType(question.id, type)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="multiple_choice">
                                    <span className="flex items-center gap-2">
                                      <CircleDot className="h-4 w-4" />
                                      Multiple Choice
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="true_false">
                                    <span className="flex items-center gap-2">
                                      <ToggleLeft className="h-4 w-4" />
                                      True/False
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="multi_select">
                                    <span className="flex items-center gap-2">
                                      <CheckSquare className="h-4 w-4" />
                                      Multi-Select
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid gap-2">
                              <Label>Points</Label>
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                value={question.points || 1}
                                onChange={(e) => updateQuestion(question.id, { points: parseInt(e.target.value) || 1 })}
                              />
                            </div>
                          </div>

                          <div className="grid gap-2">
                            <Label>Question Text *</Label>
                            <Textarea
                              value={question.question}
                              onChange={(e) =>
                                updateQuestion(question.id, { question: e.target.value })
                              }
                              placeholder="Enter your question here..."
                              rows={2}
                            />
                          </div>

                          {question.question_type === 'free_text' ? (
                            <div className="grid gap-2">
                              <Label>Answer Type</Label>
                              <div className="p-4 rounded-lg border border-dashed bg-muted/30 text-sm text-muted-foreground">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                Free text response — the learner will type their answer. Points are awarded for any non-empty response.
                              </div>
                            </div>
                          ) : (
                          <div className="grid gap-2">
                            <Label>
                              Answer Options
                              {question.question_type === 'multi_select' && (
                                <span className="text-muted-foreground font-normal ml-2">
                                  (check all correct answers)
                                </span>
                              )}
                            </Label>
                            <div className="space-y-2">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-2">
                                  {question.question_type === 'multi_select' ? (
                                    <Checkbox
                                      checked={Array.isArray(question.correct_answer) && question.correct_answer.includes(optIndex)}
                                      onCheckedChange={() => toggleMultiSelectAnswer(question.id, optIndex)}
                                      className="h-8 w-8"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                                        question.correct_answer === optIndex
                                          ? "bg-green-500 border-green-500 text-white"
                                          : "border-muted-foreground/30 hover:border-green-500"
                                      )}
                                      onClick={() =>
                                        updateQuestion(question.id, { correct_answer: optIndex })
                                      }
                                      title="Mark as correct answer"
                                    >
                                      {String.fromCharCode(65 + optIndex)}
                                    </button>
                                  )}
                                  {question.question_type === 'true_false' ? (
                                    <div className={cn(
                                      "flex-1 px-3 py-2 rounded-md border",
                                      question.correct_answer === optIndex
                                        ? "border-green-500 bg-green-50 dark:bg-green-950/20"
                                        : "border-muted"
                                    )}>
                                      {option}
                                    </div>
                                  ) : (
                                    <Input
                                      value={option}
                                      onChange={(e) =>
                                        updateOption(question.id, optIndex, e.target.value)
                                      }
                                      placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                      className="flex-1"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {question.question_type === 'multi_select'
                                ? 'Check the boxes for all correct answers'
                                : question.question_type === 'true_false'
                                  ? 'Click True or False to mark the correct answer'
                                  : 'Click the letter to mark the correct answer (highlighted in green)'}
                            </p>
                          </div>
                          )}

                          <div className="grid gap-2">
                            <Label>Explanation (Optional)</Label>
                            <Textarea
                              value={question.explanation || ''}
                              onChange={(e) =>
                                updateQuestion(question.id, { explanation: e.target.value })
                              }
                              placeholder="Explain why this answer is correct..."
                              rows={2}
                            />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Validation summary */}
      {value.questions.length > 0 && (
        <div className="p-3 bg-muted/50 rounded-lg text-sm">
          <p className="font-medium">Quiz Summary:</p>
          <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-1">
            <li>{value.questions.length} question{value.questions.length !== 1 ? 's' : ''}</li>
            <li>Total points: {totalPoints}</li>
            <li>Passing score: {value.passing_score}%</li>
            <li>
              Shuffle: {value.shuffle_questions ? 'Questions' : ''} 
              {value.shuffle_questions && value.shuffle_options ? ' & ' : ''}
              {value.shuffle_options ? 'Options' : ''} 
              {!value.shuffle_questions && !value.shuffle_options ? 'Disabled' : ''}
            </li>
            <li>
              {value.questions.filter((q) => q.question && (
                q.question_type === 'free_text'
                  ? true
                  : q.question_type === 'multi_select'
                    ? Array.isArray(q.correct_answer) && q.correct_answer.length > 0
                    : q.options[q.correct_answer as number]
              )).length} 
              {' '}complete question{value.questions.filter((q) => q.question && (
                q.question_type === 'multi_select' 
                  ? Array.isArray(q.correct_answer) && q.correct_answer.length > 0
                  : q.options[q.correct_answer as number]
              )).length !== 1 ? 's' : ''}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

export function createEmptyQuizConfig(): QuizConfig {
  return {
    quiz_type: 'multiple_choice',
    passing_score: 70,
    questions: [],
    show_explanations: true,
    shuffle_questions: false,
    shuffle_options: false,
  };
}
